import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { track, EVENTS } from '@/utils/analytics';
import { X } from '@/components/icons';
import { Button } from '../ui/button';
import { PrntrCard } from '../ui/prntr-input';
import { UploadFileRow } from '../ui/upload-file-row';
import { DateObjectRow, SelectRowField, SwitchRowField, TextRow } from '../ui/prntr-fields';
import { DetailSkeleton } from '../ui/skeleton-presets';
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  useDocumentCreate,
  useDocumentGetById,
  useDocumentUpdate,
  useTripGetById,
  useTripLegGetById,
  useTripLegUpdateComplianceDocuments,
  useTripUpdateComplianceDocuments,
} from '@/lib/hooks';
import { useAircraftAddDocument } from '@/lib/hooks/aircraft';
import { useDocumentFileUpload } from '@/lib/hooks/useDocumentFileUpload';
import {
  MAX_DOCUMENT_FILE_SIZE,
  documentFormSchema,
  getExpiryHintStatus,
  type DocumentFormData,
} from '@/lib/documents';
import { DocumentSubType } from '@@api/shared';
import { FieldError } from '../FieldError';
import { DocumentFileCard } from './DocumentFileCard';
import { ownerConfigs } from './ownerConfig';
import type { DocumentFormOwner, StagedDocument } from './types';

// The generated oRPC hooks are untyped (known type-check baseline); local
// shapes for what this drawer reads/writes.
interface DocumentRecord {
  title: string;
  subType: string;
  documentNumber: string | null;
  issuer: string | null;
  version: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  isPublic: boolean | null;
  s3Key: string | null;
  fileName: string | null;
}

type MutateAsync = (input: Record<string, unknown>) => Promise<{ id: string }>;

interface DocumentFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  owner: DocumentFormOwner;
  documentId?: string | null; // edit mode when set
  onStage?: (doc: StagedDocument) => void; // no network: hand form data back to parent
}

// Unified create/edit drawer for every document category. The `owner` decides
// which fields render, which subTypes are offered, and which mutation saves
// (see ownerConfig.ts). Replaces the per-category *DocumentDrawer components.
export function DocumentFormDrawer({
  isOpen,
  onClose,
  owner,
  documentId,
  onStage,
}: DocumentFormDrawerProps) {
  const config = ownerConfigs[owner.type];
  const isEditMode = !!documentId;

  const [isSaving, setIsSaving] = useState(false);
  const [showNoExpiryAlert, setShowNoExpiryAlert] = useState(false);
  const pendingFormData = useRef<DocumentFormData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: documentData, isLoading } = useDocumentGetById(isEditMode ? documentId : null);
  const document = documentData as DocumentRecord | undefined;

  const { progress: uploadProgress, upload, reset: resetUpload } = useDocumentFileUpload();

  const createDocument = useDocumentCreate();
  const updateDocument = useDocumentUpdate();
  const addAircraftDocument = useAircraftAddDocument(
    owner.type === 'aircraft' ? owner.aircraftId : ''
  );

  // Trip linking (only exercised for owner.type === 'trip')
  const tripId = owner.type === 'trip' ? owner.tripId : undefined;
  const tripLegId = owner.type === 'trip' ? owner.tripLegId : undefined;
  const { data: trip } = useTripGetById(tripId ?? '');
  const { data: tripLeg } = useTripLegGetById(tripLegId);
  const updateTripDocuments = useTripUpdateComplianceDocuments(tripId ?? '');
  const updateLegDocuments = useTripLegUpdateComplianceDocuments();

  const createDocumentAsync = createDocument.mutateAsync as unknown as MutateAsync;
  const updateDocumentAsync = updateDocument.mutateAsync as unknown as MutateAsync;
  const addAircraftDocumentAsync = addAircraftDocument.mutateAsync as unknown as MutateAsync;
  const updateTripDocumentsAsync = updateTripDocuments.mutateAsync as unknown as MutateAsync;
  const updateLegDocumentsAsync = updateLegDocuments.mutateAsync as unknown as MutateAsync;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<DocumentFormData>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      title: '',
      subType: config.defaultSubType,
      documentNumber: '',
      issuer: '',
      version: '',
      issueDate: undefined,
      expiryDate: undefined,
      isPublic: false,
      file: null,
    },
    values: isEditMode && document ? {
      title: document.title,
      subType: document.subType as DocumentSubType,
      documentNumber: document.documentNumber || '',
      issuer: document.issuer || '',
      version: document.version || '',
      issueDate: document.issueDate ? new Date(document.issueDate) : undefined,
      expiryDate: document.expiryDate ? new Date(document.expiryDate) : undefined,
      isPublic: document.isPublic ?? false,
      file: null,
    } : undefined,
    resetOptions: {
      keepDirtyValues: isEditMode,
    },
    mode: 'onChange',
  });

  const file = watch('file');
  const expiryStatus = getExpiryHintStatus(watch('expiryDate'));
  const isBusy = isSaving || isSubmitting;

  // Reset form when drawer opens in create mode
  useEffect(() => {
    if (isOpen && !isEditMode) {
      reset();
      resetUpload();
      setIsSaving(false);
    }
  }, [isOpen, isEditMode, reset, resetUpload]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_DOCUMENT_FILE_SIZE) {
        toast.error(`${selectedFile.name} is larger than 150MB and cannot be uploaded`);
        return;
      }
      setValue('file', selectedFile, { shouldValidate: true, shouldDirty: true });
      // Auto-set title from filename in create mode if title is empty
      if (!isEditMode && !watch('title')) {
        setValue('title', selectedFile.name, { shouldValidate: true, shouldDirty: true });
      }
    }
    e.target.value = '';
  };

  const handleRemoveFile = () => {
    setValue('file', null, { shouldValidate: true, shouldDirty: true });
    resetUpload();
  };

  const ownerId =
    owner.type === 'crew' ? owner.crewId
    : owner.type === 'passenger' ? owner.passengerId
    : owner.type === 'aircraft' ? owner.aircraftId
    : ''; // compliance/trip keys are per-subType, not per-record

  const uploadToS3 = async (fileToUpload: File, subType: DocumentSubType | undefined) => {
    const s3Key = config.generateS3Key(
      ownerId ?? '',
      subType ?? config.defaultSubType ?? DocumentSubType.Other,
      fileToUpload.name
    );
    await upload(fileToUpload, s3Key);
    return s3Key;
  };

  const onSubmit = async (data: DocumentFormData) => {
    if (config.fields.confirmMissingExpiry && !data.expiryDate) {
      pendingFormData.current = data;
      setShowNoExpiryAlert(true);
      return;
    }
    await saveDocument(data);
  };

  const createByOwner = async (data: DocumentFormData, s3Key: string, fileName: string) => {
    const common = {
      title: data.title.trim() || fileName,
      subType: data.subType,
      s3Key,
      fileName,
      documentNumber: data.documentNumber?.trim() || undefined,
      issuer: data.issuer?.trim() || undefined,
      issueDate: data.issueDate ?? undefined,
      expiryDate: data.expiryDate ?? undefined,
      isPublic: data.isPublic,
    };

    switch (owner.type) {
      case 'crew':
        return createDocumentAsync({ ...common, category: 'Crew', crewId: owner.crewId });
      case 'passenger':
        return createDocumentAsync({
          ...common,
          category: 'Passenger',
          passengerId: owner.passengerId,
        });
      case 'aircraft':
        return addAircraftDocumentAsync({ ...common, aircraftId: owner.aircraftId });
      case 'compliance':
        return createDocumentAsync({
          category: 'Compliance',
          title: data.title.trim(),
          subType: data.subType ?? DocumentSubType.Manual,
          version: data.version?.trim() || undefined,
          expiryDate: data.expiryDate ?? undefined,
          s3Key,
          fileName,
        });
      case 'trip': {
        const newDocument = await createDocumentAsync({
          ...common,
          category: 'Compliance',
          subType: data.subType ?? DocumentSubType.Manual,
          version: '1',
        });
        // Link the new document to the flight or the trip
        if (tripLegId) {
          const existing = (tripLeg as { documents?: { id: string }[] } | undefined)?.documents ?? [];
          await updateLegDocumentsAsync({
            tripLegId,
            documentIds: [...existing.map((d) => d.id), newDocument.id],
          });
        } else if (tripId) {
          const existing = (trip as { documents?: { id: string }[] } | undefined)?.documents ?? [];
          await updateTripDocumentsAsync({
            tripId,
            documentIds: [...existing.map((d) => d.id), newDocument.id],
          });
        }
        return newDocument;
      }
    }
  };

  const saveDocument = async (data: DocumentFormData) => {
    // Staging mode (create wizard, the owning record doesn't exist yet): hand
    // the form values back to the parent and skip the network. Sits here rather
    // than in onSubmit so the expiry-date dialog's Continue also stages.
    if (onStage && !isEditMode) {
      if (!data.file) {
        toast.error('Missing required data');
        return;
      }
      onStage({
        file: data.file,
        title: data.title.trim() || data.file.name,
        subType: data.subType,
        documentNumber: data.documentNumber?.trim() || undefined,
        issuer: data.issuer?.trim() || undefined,
        issueDate: data.issueDate ?? null,
        expiryDate: data.expiryDate ?? null,
        isPublic: data.isPublic,
      });
      toast.success('Document added');
      reset();
      resetUpload();
      onClose();
      return;
    }

    if (isEditMode) {
      if (!documentId || !document) return;

      setIsSaving(true);
      try {
        let s3Key = document.s3Key;
        let fileName = document.fileName;

        // If a new file was selected, upload it first
        if (data.file) {
          s3Key = await uploadToS3(data.file, data.subType);
          fileName = data.file.name;
        }

        await updateDocumentAsync({
          id: documentId,
          title: data.title.trim(),
          subType: data.subType,
          fileName: fileName ?? undefined,
          s3Key: s3Key ?? undefined,
          // Cleared strings must be '' (the service re-coalesces null away) and
          // cleared dates must be null (Prisma ignores undefined keys) — else
          // clearing a field silently keeps the old value.
          ...(config.fields.documentNumber && {
            documentNumber: data.documentNumber?.trim() || '',
          }),
          ...(config.fields.issuer && { issuer: data.issuer?.trim() || '' }),
          ...(config.fields.version && { version: data.version?.trim() || '' }),
          ...(config.fields.issueDate && { issueDate: data.issueDate ?? null }),
          expiryDate: data.expiryDate ?? null,
          ...(config.fields.isPublic && { isPublic: data.isPublic }),
        });

        toast.success('Document updated successfully');
        resetUpload();
        setIsSaving(false);
        onClose();
      } catch (error) {
        console.error('Update error:', error);
        resetUpload();
        setIsSaving(false);
        toast.error(error instanceof Error ? error.message : 'Failed to update document');
      }
    } else {
      // Create mode
      if (!data.file || (owner.type !== 'compliance' && owner.type !== 'trip' && !ownerId)) {
        toast.error('Missing required data');
        return;
      }

      setIsSaving(true);
      try {
        const s3Key = await uploadToS3(data.file, data.subType);
        await createByOwner(data, s3Key, data.file.name);

        track(EVENTS.DOCUMENT_UPLOADED, { category: config.category, subType: data.subType });
        toast.success('Document uploaded successfully');

        setTimeout(() => {
          reset();
          resetUpload();
          setIsSaving(false);
          onClose();
        }, 500);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload document');
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    if (isBusy) return;
    reset();
    resetUpload();
    onClose();
  };

  // In create mode, file is required for submit
  const canSubmit = isEditMode
    ? isDirty && isValid && !isBusy && !isLoading
    : isDirty && isValid && !isBusy && !!file;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DrawerContent className="bg-surface-L0 flex flex-col max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 shrink-0">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {isEditMode ? config.editTitle : 'Upload Document'}
          </DrawerTitle>
          <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={handleCancel} disabled={isBusy}>
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        {/* Scrollable Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {isEditMode && isLoading ? (
            <DetailSkeleton rows={6} />
          ) : (
            <form id="document-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <PrntrCard>
                <TextRow nullable={false}
                  control={control}
                  name="title"
                  label="Document Title *"
                  placeholder="e.g., British Passport"
                  readOnly={isBusy}
                  error={errors.title?.message}
                />
                <SelectRowField
                  control={control}
                  name="subType"
                  label="Document Type"
                  options={config.subTypeOptions}
                  placeholder="Select document type"
                  readOnly={isBusy}
                  error={errors.subType?.message}
                />
                {config.fields.version && (
                  <TextRow nullable={false}
                    control={control}
                    name="version"
                    label="Version Number"
                    readOnly={isBusy}
                    error={errors.version?.message}
                  />
                )}
                {config.fields.documentNumber && (
                  <TextRow nullable={false}
                    control={control}
                    name="documentNumber"
                    label="Document Number"
                    placeholder="e.g., SING394840"
                    readOnly={isBusy}
                    error={errors.documentNumber?.message}
                  />
                )}
                {config.fields.issuer && (
                  <TextRow nullable={false}
                    control={control}
                    name="issuer"
                    label="Issuer"
                    placeholder="e.g., US Embassy"
                    readOnly={isBusy}
                    error={errors.issuer?.message}
                  />
                )}
                {config.fields.issueDate && (
                  <DateObjectRow
                    control={control}
                    name="issueDate"
                    label="Issue Date"
                    readOnly={isBusy}
                    clearable
                    error={errors.issueDate?.message}
                  />
                )}
                {config.fields.expiryHints ? (
                  /* Expiry keeps its minDate bound + expiry-status hints below
                     the row, so the hairline moves under the hint text */
                  <div>
                    <DateObjectRow
                      control={control}
                      name="expiryDate"
                      label="Expiry Date"
                      title="Expiry Date"
                      minDate={watch('issueDate')}
                      readOnly={isBusy}
                      clearable
                      error={errors.expiryDate?.message}
                      className="border-b-0"
                    />
                    {expiryStatus === 'expired' && (
                      <p className="pb-2 text-xs text-content-negative-bold">Document has expired</p>
                    )}
                    {expiryStatus === 'expiring' && (
                      <p className="pb-2 text-xs text-background-notice-bold">
                        Document expires within a month
                      </p>
                    )}
                    <div className="border-b border-border-L1" />
                  </div>
                ) : (
                  <DateObjectRow
                    control={control}
                    name="expiryDate"
                    label="Expiry Date"
                    readOnly={isBusy}
                    error={errors.expiryDate?.message}
                  />
                )}
                {config.fields.isPublic && (
                  <SwitchRowField
                    control={control}
                    name="isPublic"
                    label="Allow access to anyone"
                    readOnly={isBusy}
                  />
                )}
              </PrntrCard>

              {/* File Section */}
              <div className="flex flex-col gap-2">
                <label className="font-medium text-sm text-content-primary-bold tracking-[0.07px]">
                  File <span className="text-content-negative-bold">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  id="document-form-file-upload"
                  accept={config.fileAccept}
                  onChange={handleFileSelect}
                  disabled={isBusy}
                  className="hidden"
                />

                <DocumentFileCard
                  file={file}
                  existingFileName={isEditMode ? document?.fileName : undefined}
                  progress={uploadProgress}
                  replaceInputId="document-form-file-upload"
                  onRemove={handleRemoveFile}
                  disabled={isBusy}
                />

                {/* Upload area (no file selected, nothing to replace) */}
                {!file && !(isEditMode && document?.fileName) && (
                  <UploadFileRow
                    onSelect={() => fileInputRef.current?.click()}
                    disabled={isBusy}
                  />
                )}
                <FieldError message={errors.file?.message} />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <DrawerFooter>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="mini"
              onClick={handleCancel}
              disabled={isBusy}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="mini"
              form="document-form"
              disabled={!canSubmit}
              className="flex-1"
            >
              {isBusy ? (isEditMode ? 'Saving...' : 'Uploading...') : 'Save'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>

      {config.fields.confirmMissingExpiry && (
        <AlertDialog open={showNoExpiryAlert} onOpenChange={setShowNoExpiryAlert}>
          <AlertDialogContent className="bg-surface-L0">
            <AlertDialogHeader>
              <AlertDialogTitle>Expiry Date Missing</AlertDialogTitle>
              <AlertDialogDescription>
                You will not be notified when this document expires.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => { pendingFormData.current = null; }}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (pendingFormData.current) {
                    await saveDocument(pendingFormData.current);
                    pendingFormData.current = null;
                  }
                }}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Drawer>
  );
}
